import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useProgressStore } from '../stores/progressStore'
import { moduleService, labService, capstoneService } from '../services'
import CircularProgress from '../components/CircularProgress'
import LearningPath from '../components/LearningPath'
import StreakCounter from '../components/StreakCounter'
import TimeEstimateBadge from '../components/TimeEstimateBadge'
import BookmarkButton from '../components/BookmarkButton'
import TierUpgradePrompt from '../components/TierUpgradePrompt'
import { canAccessCertificates, canAccessPremiumResources, getTierDisplayName } from '../utils/tierPermissions'
import {
  BookOpenIcon,
  BeakerIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  ClockIcon,
  AcademicCapIcon,
  FireIcon,
  TrophyIcon,
  ArrowRightIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  SparklesIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid'

export default function Dashboard() {
  const { user } = useAuthStore()
  const { progress, fetchProgress, getCompletionPercentage } = useProgressStore()
  const location = useLocation()
  const [allModules, setAllModules] = useState([])
  const [recentModules, setRecentModules] = useState([])
  const [nextModule, setNextModule] = useState(null)
  const [activeLabs, setActiveLabs] = useState([])
  const [activeCapstones, setActiveCapstones] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSetupBanner, setShowSetupBanner] = useState(true)
  const [showCertificateUpgrade, setShowCertificateUpgrade] = useState(false)
  const [showPremiumUpgrade, setShowPremiumUpgrade] = useState(false)

  // Tier permissions
  const userTier = user?.enrolled_tier
  const hasCertificateAccess = canAccessCertificates(userTier)
  const hasPremiumAccess = canAccessPremiumResources(userTier)
  const isInstructor = user?.role === 'instructor'
  const tierDisplayName = isInstructor ? 'Instructor' : (user?.is_admin ? 'Administrator' : getTierDisplayName(userTier))

  useEffect(() => {
    fetchProgress()
    fetchDashboardData()
    
    // Check if user was redirected from a protected route
    if (location.state?.deniedAccess && location.state?.requiredFeature) {
      if (location.state.requiredFeature === 'certificates') {
        setShowCertificateUpgrade(true)
      } else if (location.state.requiredFeature === 'premium_templates') {
        setShowPremiumUpgrade(true)
      }
    }
    
    // Check if user has dismissed the setup banner
    const dismissed = localStorage.getItem('setupBannerDismissed')
    setShowSetupBanner(!dismissed)
  }, [fetchProgress, location.state])

  const dismissSetupBanner = () => {
    localStorage.setItem('setupBannerDismissed', 'true')
    setShowSetupBanner(false)
  }

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      // Fetch all content
      const [modulesData, labsData, capstonesData] = await Promise.all([
        moduleService.getAll(),
        labService.getAll(),
        capstoneService.getAll(),
      ])

      // Store all modules for learning path
      setAllModules(modulesData)

      // Find next module (first incomplete one)
      const nextIncomplete = modulesData.find(m => m.progress?.status !== 'completed')
      setNextModule(nextIncomplete || null)

      // Get recent modules (in progress or completed, sorted by last activity)
      const recentMods = modulesData
        .filter(m => m.progress?.status === 'in_progress' || m.progress?.status === 'completed')
        .slice(0, 3)
      setRecentModules(recentMods)

      // Get active labs (in progress)
      const activeLab = labsData.filter(l => l.progress?.status === 'in_progress').slice(0, 2)
      setActiveLabs(activeLab)

      // Get active capstones (in progress)
      const activeCap = capstonesData.filter(c => c.progress?.status === 'in_progress').slice(0, 2)
      setActiveCapstones(activeCap)

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const completionPercentage = getCompletionPercentage()

  // Calculate component-specific percentages
  const modulePercentage = progress?.total_modules 
    ? Math.round((progress.completed_modules / progress.total_modules) * 100)
    : 0
  const labPercentage = progress?.total_labs
    ? Math.round((progress.completed_labs / progress.total_labs) * 100)
    : 0
  const capstonePercentage = progress?.total_capstones
    ? Math.round((progress.completed_capstones / progress.total_capstones) * 100)
    : 0

  // Calculate streak (placeholder - you can enhance this with actual data)
  const learningStreak = 0 // Will be calculated from actual progress data

  // Milestone messages
  const getMilestoneMessage = () => {
    if (completionPercentage === 100) return { text: '🎉 Course Complete! You\'re a master!', color: 'text-green-600' }
    if (completionPercentage >= 75) return { text: '🚀 Almost there! Final stretch!', color: 'text-purple-600' }
    if (completionPercentage >= 50) return { text: '⭐ Halfway done! Keep going!', color: 'text-blue-600' }
    if (completionPercentage >= 25) return { text: '💪 Great start! You\'re making progress!', color: 'text-yellow-600' }
    return { text: '👋 Welcome! Begin your learning journey!', color: 'text-gray-600' }
  }

  const milestone = getMilestoneMessage()

  // Build quick links array - only include certificate link if user has access
  const quickLinks = [
    {
      name: 'Continue Learning',
      description: nextModule ? `Next: ${nextModule.title}` : 'Start your first module',
      href: nextModule ? `/modules/${nextModule.id}` : '/modules',
      icon: BookOpenIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Practice Labs',
      description: activeLabs.length > 0 ? `${activeLabs.length} in progress` : 'Hands-on exercises',
      href: '/labs',
      icon: BeakerIcon,
      color: 'bg-purple-500',
    },
    {
      name: 'Capstone Projects',
      description: activeCapstones.length > 0 ? `${activeCapstones.length} active` : 'Build real applications',
      href: '/capstones',
      icon: RocketLaunchIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Premium Templates',
      description: (hasPremiumAccess || user?.is_admin || user?.role === 'instructor') ? '10 production templates' : 'Upgrade to unlock',
      href: '/premium-templates',
      icon: SparklesIcon,
      color: 'bg-indigo-500',
    },
    // Only show certificate link if user has certificate access (Complete tier+)
    ...(hasCertificateAccess ? [{
      name: 'My Certificate',
      description: completionPercentage === 100 
        ? 'Ready to download!' 
        : 'Complete course first',
      href: '/certificate',
      icon: AcademicCapIcon,
      color: 'bg-yellow-500',
      disabled: completionPercentage < 100,
    }] : []),
  ]

  return (
    <div className="space-y-8">
      {/* Getting Started Setup Banner */}
      {showSetupBanner && completionPercentage < 10 && (
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  🚀 Before You Start: Required Setup
                </h3>
                <p className="text-gray-700 mb-4">
                  To complete the labs and capstones, you'll need these two accounts:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-orange-100">
                    <div className="flex items-center mb-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      <h4 className="font-semibold text-gray-900">OpenAI API Key</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Required for all AI labs (~$5-20 total cost for the course)
                    </p>
                    <a
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Get API Key →
                    </a>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-orange-100">
                    <div className="flex items-center mb-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      <h4 className="font-semibold text-gray-900">GitHub Account</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Free account for code submissions and portfolio building
                    </p>
                    <a
                      href="https://github.com/signup"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Sign Up Free →
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={dismissSetupBanner}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 ml-4"
              aria-label="Dismiss"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-cte-blue to-cte-blue-dark rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 opacity-10">
          <TrophyIcon className="h-48 w-48" />
        </div>
        <div className="relative">
          <h1 className="text-3xl font-bold">Welcome back, {user?.name}! 👋</h1>
          <p className="mt-2 text-white/90 text-lg">
            {completionPercentage === 100 
              ? "Congratulations! You've mastered the course! 🎉"
              : completionPercentage >= 50
              ? `Great progress! You're ${completionPercentage}% complete.`
              : `Ready to continue your AI journey? You're ${completionPercentage}% complete.`
            }
          </p>
          <div className="mt-4">
            <StreakCounter />
          </div>
        </div>
      </div>

      {/* Progress Overview with Circular Progress */}
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <h2 className="text-xl font-bold text-cte-black mb-6 flex items-center">
          <ChartBarIcon className="h-6 w-6 mr-2 text-cte-blue" />
          Your Progress Overview
        </h2>
        
        {/* Circular Progress Rings */}
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Overall Progress - Large Ring */}
          <div className="md:col-span-1 flex flex-col items-center">
            <CircularProgress
              percentage={completionPercentage}
              size={140}
              strokeWidth={12}
              color="#2563eb"
              backgroundColor="#e5e7eb"
              showPercentage={true}
              label="Overall"
              animationDuration={1500}
            />
            <p className={`mt-4 text-center font-semibold ${milestone.color}`}>
              {milestone.text}
            </p>
          </div>

          {/* Component Progress - Smaller Rings */}
          <div className="md:col-span-3 grid grid-cols-3 gap-6">
            <div className="flex flex-col items-center">
              <CircularProgress
                percentage={modulePercentage}
                size={100}
                strokeWidth={8}
                color="#3b82f6"
                backgroundColor="#e5e7eb"
                showPercentage={true}
                animationDuration={1200}
              />
              <div className="mt-3 text-center">
                <p className="font-semibold text-gray-900">Modules</p>
                <p className="text-sm text-gray-600">
                  {progress?.completed_modules || 0}/{progress?.total_modules || 10}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <CircularProgress
                percentage={labPercentage}
                size={100}
                strokeWidth={8}
                color="#a855f7"
                backgroundColor="#e5e7eb"
                showPercentage={true}
                animationDuration={1200}
              />
              <div className="mt-3 text-center">
                <p className="font-semibold text-gray-900">Labs</p>
                <p className="text-sm text-gray-600">
                  {progress?.completed_labs || 0}/{progress?.total_labs || 4}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <CircularProgress
                percentage={capstonePercentage}
                size={100}
                strokeWidth={8}
                color="#22c55e"
                backgroundColor="#e5e7eb"
                showPercentage={true}
                animationDuration={1200}
              />
              <div className="mt-3 text-center">
                <p className="font-semibold text-gray-900">Capstones</p>
                <p className="text-sm text-gray-600">
                  {progress?.completed_capstones || 0}/{progress?.total_capstones || 4}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Linear progress bar for detail */}
        <div className="progress-bar h-3">
          <div 
            className="progress-fill transition-all duration-1000" 
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <div className="flex justify-between items-center mt-2 text-sm text-gray-600">
          <span>Start</span>
          <span className="font-semibold text-cte-blue">{completionPercentage}% Complete</span>
          <span>Certificate</span>
        </div>
      </div>

      {/* Learning Path */}
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <h2 className="text-xl font-bold text-cte-black mb-6 flex items-center">
          <BookOpenIcon className="h-6 w-6 mr-2 text-cte-blue" />
          Your Learning Path
        </h2>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading your path...</div>
        ) : (
          <LearningPath modules={allModules} currentModuleId={nextModule?.id} />
        )}
      </div>

      {/* Progress Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Modules Completed</p>
              <p className="text-2xl font-bold text-cte-black mt-1">
                {progress?.completed_modules || 0}/{progress?.total_modules || 10}
              </p>
            </div>
            <BookOpenIcon className="h-12 w-12 text-cte-blue opacity-20" />
          </div>
          <div className="mt-4 progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${modulePercentage}%` 
              }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Labs Completed</p>
              <p className="text-2xl font-bold text-cte-black mt-1">
                {progress?.completed_labs || 0}/{progress?.total_labs || 4}
              </p>
            </div>
            <BeakerIcon className="h-12 w-12 text-purple-500 opacity-20" />
          </div>
          <div className="mt-4 progress-bar">
            <div 
              className="progress-fill bg-purple-500" 
              style={{ 
                width: `${labPercentage}%` 
              }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Capstones Submitted</p>
              <p className="text-2xl font-bold text-cte-black mt-1">
                {progress?.completed_capstones || 0}/{progress?.total_capstones || 4}
              </p>
            </div>
            <RocketLaunchIcon className="h-12 w-12 text-green-500 opacity-20" />
          </div>
          <div className="mt-4 progress-bar">
            <div 
              className="progress-fill bg-green-500" 
              style={{ 
                width: `${capstonePercentage}%` 
              }}
            />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-bold text-cte-black mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className={`bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all ${
                link.disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
              }`}
            >
              <div className={`${link.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                <link.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-cte-black mb-1">{link.name}</h3>
              <p className="text-sm text-gray-600">{link.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Tier Upgrade Prompts */}
      <TierUpgradePrompt 
        feature="certificates"
        currentTier={userTier}
        show={showCertificateUpgrade}
        onClose={() => setShowCertificateUpgrade(false)}
        variant="modal"
      />

      <TierUpgradePrompt 
        feature="premium_templates"
        currentTier={userTier}
        show={showPremiumUpgrade}
        onClose={() => setShowPremiumUpgrade(false)}
        variant="modal"
      />

      {/* Current Tier Badge */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-indigo-600 w-12 h-12 rounded-full flex items-center justify-center">
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Your Current Plan</p>
              <h3 className="text-2xl font-bold text-gray-900">{tierDisplayName}</h3>
            </div>
          </div>
          {userTier !== 'elite' && !user?.is_admin && !isInstructor && (
            <a
              href="/#pricing"
              className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              Upgrade Plan
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </a>
          )}
        </div>

        {/* Feature highlights based on tier */}
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-start gap-2">
            <CheckCircleSolid className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">All Course Content</p>
              <p className="text-xs text-gray-600">10 Modules, 4 Labs, 4 Capstones</p>
            </div>
          </div>
          
          {hasCertificateAccess && (
            <div className="flex items-start gap-2">
              <CheckCircleSolid className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Certificates</p>
                <p className="text-xs text-gray-600">Course + Module completion</p>
              </div>
            </div>
          )}
          
          {hasPremiumAccess && (
            <>
              <div className="flex items-start gap-2">
                <CheckCircleSolid className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Premium Templates</p>
                  <p className="text-xs text-gray-600">Production-ready code</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleSolid className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Exclusive Case Studies</p>
                  <p className="text-xs text-gray-600">Real-world implementations</p>
                </div>
              </div>
            </>
          )}
          
          {!hasPremiumAccess && !user?.is_admin && !isInstructor && (
            <div className="flex items-start gap-2">
              <LockClosedIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <button
                  onClick={() => setShowPremiumUpgrade(true)}
                  className="text-sm font-medium text-amber-700 hover:text-amber-800 text-left"
                >
                  Unlock Premium Resources
                </button>
                <p className="text-xs text-gray-600">Upgrade to Premium</p>
              </div>
            </div>
          )}
          
          {user?.is_admin && !isInstructor && (
            <>
              <div className="flex items-start gap-2">
                <CheckCircleSolid className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Admin Panel</p>
                  <p className="text-xs text-gray-600">Full system access</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleSolid className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">All Premium Content</p>
                  <p className="text-xs text-gray-600">Templates & case studies</p>
                </div>
              </div>
            </>
          )}
          
          {isInstructor && (
            <>
              <div className="flex items-start gap-2">
                <CheckCircleSolid className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Teaching Access</p>
                  <p className="text-xs text-gray-600">All course materials</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleSolid className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Premium Resources</p>
                  <p className="text-xs text-gray-600">Templates & case studies</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upgrade CTA for Core tier users */}
      {userTier === 'core' && !user?.is_admin && !isInstructor && (
        <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-300 rounded-xl p-6 shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <TrophyIcon className="h-6 w-6 text-amber-600" />
                <h3 className="text-xl font-bold text-gray-900">Unlock Your Full Potential</h3>
              </div>
              <p className="text-gray-700 mb-4">
                You're on the <strong>Core Access</strong> plan. Upgrade to get certificates, premium resources, and more!
              </p>
              
              <div className="grid md:grid-cols-2 gap-3 mb-4">
                <div className="bg-white/70 rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <AcademicCapIcon className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-sm text-gray-900">Get Certificates</span>
                  </div>
                  <p className="text-xs text-gray-600">Prove your expertise with official course and module completion certificates</p>
                </div>
                
                <div className="bg-white/70 rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <SparklesIcon className="h-5 w-5 text-purple-600" />
                    <span className="font-semibold text-sm text-gray-900">Premium Templates</span>
                  </div>
                  <p className="text-xs text-gray-600">Production-ready code templates and exclusive case studies</p>
                </div>
              </div>
              
              <a
                href="/#pricing"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg"
              >
                View Upgrade Options
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </a>
              
              <p className="text-xs text-gray-600 mt-3">
                Starting at just <strong>$50 more</strong> for Complete Package
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Continue Learning */}
        {nextModule && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-cte-black">Continue Learning</h2>
              <Link to={`/modules/${nextModule.id}`} className="text-sm text-cte-blue hover:underline font-semibold">
                View Module →
              </Link>
            </div>
            <Link 
              to={`/modules/${nextModule.id}`}
              className="group block bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border-2 border-blue-200 hover:border-blue-400 transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <span className="text-xs font-semibold text-cte-blue uppercase tracking-wide">
                  {nextModule.code}
                </span>
                <span className="text-xs bg-cte-blue text-white px-2 py-1 rounded-full">
                  Up Next
                </span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-cte-blue transition-colors">
                {nextModule.title}
              </h3>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {nextModule.description}
              </p>
              <TimeEstimateBadge minutes={nextModule.estimated_minutes} />
            </Link>
          </div>
        )}

        {/* Active Work */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-cte-black mb-4">Active Work</h2>
          <div className="space-y-3">
            {activeLabs.map(lab => (
              <Link
                key={lab.id}
                to={`/labs/${lab.id}`}
                className="block p-4 bg-purple-50 rounded-lg border border-purple-200 hover:border-purple-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <BeakerIcon className="h-4 w-4 text-purple-600 mr-2" />
                      <span className="text-xs font-semibold text-purple-600">LAB</span>
                      <div className="ml-2">
                        <TimeEstimateBadge minutes={lab.estimated_minutes} className="bg-purple-100 text-purple-800" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">{lab.title}</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <BookmarkButton type="Lab" id={lab.id} size="sm" />
                    <ArrowRightIcon className="h-4 w-4 text-purple-600" />
                  </div>
                </div>
                {lab.progress?.completion_percentage > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>In Progress</span>
                      <span>{Math.round(lab.progress.completion_percentage)}%</span>
                    </div>
                    <div className="progress-bar h-1.5">
                      <div 
                        className="progress-fill bg-purple-600" 
                        style={{ width: `${lab.progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </Link>
            ))}
            
            {activeCapstones.map(capstone => (
              <Link
                key={capstone.id}
                to={`/capstones/${capstone.id}`}
                className="block p-4 bg-green-50 rounded-lg border border-green-200 hover:border-green-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <RocketLaunchIcon className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-xs font-semibold text-green-600">CAPSTONE</span>
                      <div className="ml-2">
                        <TimeEstimateBadge minutes={capstone.estimated_minutes} className="bg-green-100 text-green-800" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">{capstone.title}</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <BookmarkButton type="Capstone" id={capstone.id} size="sm" />
                    <ArrowRightIcon className="h-4 w-4 text-green-600" />
                  </div>
                </div>
                {capstone.progress?.completion_percentage > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>In Progress</span>
                      <span>{Math.round(capstone.progress.completion_percentage)}%</span>
                    </div>
                    <div className="progress-bar h-1.5">
                      <div 
                        className="progress-fill bg-green-600" 
                        style={{ width: `${capstone.progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </Link>
            ))}

            {activeLabs.length === 0 && activeCapstones.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <ClockIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No active work. Start a lab or capstone!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {recentModules.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-cte-black mb-4">Recent Activity</h2>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
            {recentModules.map((module) => (
              <Link
                key={module.id}
                to={`/modules/${module.id}`}
                className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center flex-1">
                  <BookOpenIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <p className="font-medium text-cte-black">{module.title}</p>
                      {module.progress?.status === 'completed' && (
                        <CheckCircleSolid className="h-4 w-4 text-green-500 ml-2" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">Module • {module.code}</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
